import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ApiError,
  AsyncDataItem,
  AsyncDataItemState,
  ITenantOrganisation,
  idleDataItem,
  isError,
  isLoading,
  wrapAsAsyncDataItem
} from '@pharvio/core/shared';
import { EMPTY, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { TENANT_ORGANISATION_DATA_SERVICE } from '../data';
import {
  LoadTenantOrganisation,
  UpdateTenantOrganisation
} from './tenant-organisation.actions';

export interface TenantOrganisationStateModel {
  organisation: AsyncDataItem<ITenantOrganisation | null>;
  actionInProgress: boolean;
  actionError: ApiError | null;
}

@State<TenantOrganisationStateModel>({
  name: 'tenantOrganisation',
  defaults: {
    organisation: idleDataItem(null),
    actionInProgress: false,
    actionError: null
  }
})
@Injectable()
export class TenantOrganisationState {
  private readonly svc = inject(TENANT_ORGANISATION_DATA_SERVICE);

  @Selector()
  static organisation(
    state: TenantOrganisationStateModel
  ): AsyncDataItem<ITenantOrganisation | null> {
    return state.organisation;
  }

  @Selector()
  static loading(state: TenantOrganisationStateModel): boolean {
    return isLoading(state.organisation);
  }

  @Selector()
  static error(state: TenantOrganisationStateModel): ApiError | null {
    return isError(state.organisation) ? state.organisation.error : null;
  }

  @Selector()
  static actionInProgress(state: TenantOrganisationStateModel): boolean {
    return state.actionInProgress;
  }

  @Action(LoadTenantOrganisation)
  load(ctx: StateContext<TenantOrganisationStateModel>) {
    ctx.patchState({
      organisation: wrapAsAsyncDataItem({
        data: ctx.getState().organisation.data
      })
    });
    return this.svc.getTenantOrganisation().pipe(
      tap((org) =>
        ctx.patchState({
          organisation: wrapAsAsyncDataItem({
            data: org,
            state: AsyncDataItemState.LOADED
          })
        })
      ),
      catchError((error) => {
        ctx.patchState({
          organisation: wrapAsAsyncDataItem({
            data: ctx.getState().organisation.data,
            state: AsyncDataItemState.ERROR,
            error
          })
        });
        return EMPTY;
      })
    );
  }

  @Action(UpdateTenantOrganisation)
  update(
    ctx: StateContext<TenantOrganisationStateModel>,
    action: UpdateTenantOrganisation
  ) {
    ctx.patchState({ actionInProgress: true, actionError: null });
    return this.svc.updateTenantOrganisation(action.payload).pipe(
      tap((org) =>
        ctx.patchState({
          organisation: wrapAsAsyncDataItem({
            data: org,
            state: AsyncDataItemState.LOADED
          }),
          actionInProgress: false
        })
      ),
      catchError((error) => {
        ctx.patchState({ actionInProgress: false, actionError: error });
        return throwError(() => error);
      })
    );
  }
}
